import React from "react";
import { JOURNAL_KEY, LEADER_KEY } from "../../tx/keys";
import { reportError } from "../../utils/reportError";
import { isChunkLoadError, reloadForNewBuild } from "../../utils/chunkReload";

// Root error boundary.
//
// Before this existed, a single throw anywhere in the tree unmounted the ENTIRE
// app and left a blank white page — no message, no way back, and (when the cause
// was a bad value in localStorage) it survived every reload. That is exactly how
// one malformed stored user took the whole product down.
//
// React only routes errors here from render, lifecycle methods and effects.
// It does NOT catch:
//   - errors in event handlers (they never break the tree, so they don't need to)
//   - rejected promises / async callbacks
//   - anything thrown during module evaluation, before the first render
// So this is a safety net, not a substitute for guarding the code that throws.

// Session keys cleared by "Sign out and start over". Deliberately enumerated
// rather than calling localStorage.clear(): the tx engine keeps IN-FLIGHT
// transaction records under `frdm.tx.*`, and a transaction can already be on
// chain while its journal row is the only local trace of it. A recovery button
// must never be the thing that loses that.
const SESSION_KEYS = [
  "User",
  "Token",
  "User Wallet",
  "alacrity",
  "alacrityName",
  "mnemonic",
  "privateKey",
  "publicKey",
  "referralCode",
];

const PRESERVED_KEYS = [JOURNAL_KEY, LEADER_KEY];

const styles = {
  // Inline, not SCSS: this screen has to render correctly even when the failure
  // is in the stylesheet pipeline or a lazily-loaded chunk.
  wrap: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    background: "#f7f8fa",
    color: "#1a1a1a",
  },
  card: {
    maxWidth: "520px",
    width: "100%",
    background: "#ffffff",
    borderRadius: "12px",
    padding: "32px",
    boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
  },
  title: { margin: "0 0 12px", fontSize: "20px", fontWeight: 600 },
  body: { margin: "0 0 24px", fontSize: "15px", lineHeight: 1.6, color: "#4a4a4a" },
  actions: { display: "flex", flexWrap: "wrap", gap: "12px" },
  primary: {
    background: "#044DB5",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "15px",
    cursor: "pointer",
  },
  secondary: {
    background: "transparent",
    color: "#044DB5",
    border: "1px solid #044DB5",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "15px",
    cursor: "pointer",
  },
  details: { marginTop: "24px", fontSize: "13px", color: "#6b6b6b" },
  pre: {
    marginTop: "8px",
    padding: "12px",
    background: "#f2f3f5",
    borderRadius: "6px",
    fontSize: "12px",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: "180px",
    overflow: "auto",
  },
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // A missing chunk is a superseded deploy, not a defect: the file the running
    // bundle asks for was deleted when a newer build replaced it. Reload and the
    // user gets the new build and continues. Reporting it as a crash would bury
    // real defects under one alert per deploy per open tab.
    if (isChunkLoadError(error) && reloadForNewBuild()) return;

    // Routed through the reporting seam so this becomes a real alert the moment
    // a crash-reporting service is wired up in utils/reportError.js. Until then
    // it lands in console.error, which survives the production console.log stub.
    reportError(error, {
      kind: "react.boundary",
      componentStack: info?.componentStack,
    });
  }

  handleRetry = () => {
    // Cheapest recovery: drop the error and re-render. Works when the throw came
    // from transient state; if the cause is still there we land right back here.
    this.setState({ error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleResetSession = () => {
    try {
      const preserved = PRESERVED_KEYS.map((k) => [k, localStorage.getItem(k)]);
      SESSION_KEYS.forEach((k) => localStorage.removeItem(k));
      localStorage.setItem("isLogout", true);
      // Belt and braces: if anything above went sideways, put the transaction
      // journal back exactly as it was.
      preserved.forEach(([k, v]) => {
        if (v !== null && localStorage.getItem(k) === null) {
          localStorage.setItem(k, v);
        }
      });
    } catch (e) {
      console.error("[ErrorBoundary] could not clear session:", e);
    }
    window.location.replace("/");
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // We only get here for a chunk error if the reload already happened and the
    // file is STILL missing — a genuinely broken deploy. "Try again" cannot fix
    // that (React caches the rejected import, so it re-throws the same error),
    // so say what is actually wrong instead of blaming the page.
    const staleBuild = isChunkLoadError(error);

    return (
      <div style={styles.wrap}>
        <div style={styles.card} role="alert">
          <h1 style={styles.title}>
            {staleBuild ? "This page is out of date" : "Something went wrong"}
          </h1>
          <p style={styles.body}>
            {staleBuild
              ? "A newer version of FRDM was deployed and part of this page could not be loaded. Your funds and transactions are not affected — nothing was changed on chain. Reload to get the latest version."
              : "This page hit an unexpected error. Your funds and transactions are not affected — nothing was changed on chain. Try again, or reload the page."}
          </p>
          <div style={styles.actions}>
            {!staleBuild && (
              <button type="button" style={styles.primary} onClick={this.handleRetry}>
                Try again
              </button>
            )}
            <button
              type="button"
              style={staleBuild ? styles.primary : styles.secondary}
              onClick={this.handleReload}
            >
              Reload page
            </button>
            <button
              type="button"
              style={styles.secondary}
              onClick={this.handleResetSession}
            >
              Sign out and start over
            </button>
          </div>
          <details style={styles.details}>
            <summary>Technical details</summary>
            <pre style={styles.pre}>{String(error?.stack || error)}</pre>
          </details>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
